import { Injectable } from '@angular/core';
import { BaseEquipmentService } from './base-equipment.service';
import {
  SolarModule,
  CreateModuleDto,
  UpdateModuleDto,
  EquipmentListResponse,
} from '@features/catalog/models';

/**
 * Module Service
 * Handles all module-related API operations
 * Endpoint: /organizations/{orgId}/equipment/modules
 */
@Injectable({
  providedIn: 'root',
})
export class ModuleService extends BaseEquipmentService<
  SolarModule,
  CreateModuleDto,
  UpdateModuleDto
> {
  private readonly equipmentType = 'modules' as const;

  /**
   * Get all modules for an organization
   */
  async getModules(
    organizationId: string
  ): Promise<EquipmentListResponse<SolarModule>> {
    return this.getAll(organizationId, this.equipmentType);
  }

  /**
   * Get a specific module by ID
   */
  async getModuleById(
    organizationId: string,
    moduleId: string
  ): Promise<SolarModule> {
    return this.getById(organizationId, this.equipmentType, moduleId);
  }

  /**
   * Create a new module
   */
  async createModule(
    organizationId: string,
    data: CreateModuleDto
  ): Promise<SolarModule> {
    return this.create(organizationId, this.equipmentType, data);
  }

  /**
   * Update an existing module
   */
  async updateModule(
    organizationId: string,
    moduleId: string,
    data: UpdateModuleDto
  ): Promise<SolarModule> {
    return this.update(organizationId, this.equipmentType, moduleId, data);
  }

  /**
   * Delete a module (soft delete)
   */
  async deleteModule(organizationId: string, moduleId: string): Promise<void> {
    return this.delete(organizationId, this.equipmentType, moduleId);
  }

  /**
   * Sync global modules from Solar Insight
   */
  async syncGlobalModules(
    organizationId: string
  ): Promise<{ synced: number; failed: number }> {
    return this.sync<{ synced: number; failed: number }>(
      organizationId,
      this.equipmentType
    );
  }

  /**
   * Duplicate a module (for backward compatibility)
   */
  async duplicateModule(
    organizationId: string,
    moduleId: string,
    newName?: string
  ): Promise<SolarModule> {
    const module = await this.getModuleById(organizationId, moduleId);
    const duplicateData: CreateModuleDto = {
      name: newName || `${module.name} (Kopie)`,
      power: module.power,
      costs: module.costs,
      installationCosts: module.installationCosts,
      mountingSystemCosts: module.mountingSystemCosts,
      height: module.height,
      width: module.width,
      efficiencyLossPerYear: module.efficiencyLossPerYear,
      hubMetadata: module.hubMetadata,
    };
    return this.createModule(organizationId, duplicateData);
  }
}
